/**
 * ReportTypePicker — first step of V2 super_admin authoring.
 * Lists platform report types from the catalog; selecting one loads defaults.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { Icons } from '../../common/Icons';
import { reportCatalogService } from '../../../services/api';

const ReportTypePicker = ({ targetTenantId, onSelect, selectedId }) => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await reportCatalogService.getReportTypes(targetTenantId);
        setTypes(res.data || []);
      } catch (err) {
        console.error('Failed to load report types', err);
        setError('Failed to load report type catalog.');
      } finally {
        setLoading(false);
      }
    })();
  }, [targetTenantId]);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const t of types) {
      const cat = t.category || 'General';
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(t);
    }
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [types]);

  if (loading) {
    return (
      <div className="empty-state">
        <Icons.Loader size={32} className="spin" />
        <p>Loading report types…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-warning">
        <Icons.AlertCircle />
        <span>{error}</span>
      </div>
    );
  }

  return (
    <div>
      <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginBottom: '16px' }}>
        Choose a report type to deploy for this tenant. Configuration options depend on the type.
      </p>
      {grouped.map(([category, items]) => (
        <div key={category} style={{ marginBottom: '20px' }}>
          <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            {category}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '12px' }}>
            {items.map((t) => {
              const active = selectedId === t.id;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => onSelect(t)}
                  style={{
                    textAlign: 'left',
                    padding: '14px',
                    borderRadius: '8px',
                    border: active ? '2px solid var(--accent-primary)' : '1px solid var(--border-color)',
                    background: active ? 'var(--bg-hover)' : 'var(--bg-card)',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                    display: 'block',
                    width: '100%',
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: '4px', color: 'var(--text-primary)' }}>{t.label}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: 1.45 }}>
                    {t.description}
                  </div>
                  <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '8px' }}>
                    {t.archetype}{t.pivot_mode ? ` · ${t.pivot_mode}` : ''}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default ReportTypePicker;
